import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LoadingObject,
  LoadingState,
} from '../../../../../common/global/models';
import Dialog from '../../../../../common/widgets/Dialog';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { rewriteImageUrl } from '../../../helpers';
import { Category, CategoryDetails } from '../../../models/Categories';
import { resources } from '../../../resources/resources';

export interface FixedBenefitDialogProps {
  benefit: Category;
  benefitDetails: LoadingObject<CategoryDetails>;
  onClose: () => void;
}

const defaultWidth = 360;
const marginWidth = 200;
const getMaxStyleWidth = (maxStyleWidth: number): string => {
  const padding = 60;

  const width = Math.min(
    maxStyleWidth + padding,
    window.innerWidth - marginWidth + padding,
  );

  return `${width}px`;
};

const getWidth = (width: number): string => {
  const newWidth = Math.min(width, window.innerWidth - marginWidth);

  return `${newWidth}px`;
};

export default function BenefitDetailsDialog(
  props: Readonly<FixedBenefitDialogProps>,
) {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<any>(null);
  const [width, setWidth] = useState<string>(getWidth(defaultWidth));
  const [isContentVisible, setIsContentVisible] = useState(false);

  useEffect(() => {
    if (
      !props.benefitDetails.value?.description ||
      !contentRef.current ||
      !dialogRef.current
    ) {
      return;
    }

    const elementsWithStyleWidth = Array.from(
      contentRef.current.children,
    ).filter(e => (e as HTMLElement).style.width);

    if (!elementsWithStyleWidth.length) {
      const contentWidth = dialogRef.current.offsetWidth;
      const contentHeight = dialogRef.current.offsetHeight;

      if (contentHeight > contentWidth) {
        setWidth(getWidth((contentHeight / contentWidth) * contentWidth));
      }

      setIsContentVisible(true);
      return;
    }

    const maxStyleWidth = elementsWithStyleWidth
      .map(e => Number.parseInt((e as HTMLElement).style.width.slice(0, -2)))
      .reduce((a, b) => Math.max(a, b), 0);

    setWidth(getMaxStyleWidth(maxStyleWidth));
    setIsContentVisible(true);
  }, [props.benefitDetails]);

  return (
    <Dialog
      closeDialog={props.onClose}
      closeOnClickOutside
      showCloseIcon
      width={width}
      dialogContentRef={dialogRef}
    >
      <Loader
        loading={props.benefitDetails?.state != LoadingState.Ready}
        loader={<LoadingSpinner />}
      >
        <div className="dialog-content">
          <div className="dialog-image category-image">
            <div className="card-img">
              <img
                alt={props.benefit.name}
                src={rewriteImageUrl(
                  props.benefit.image2 || props.benefit.image,
                )}
              />
            </div>
          </div>
          <div className="dialog-title">
            {_.unescape(props.benefit.name)}
            {customerInfo?.isImpersonating && `(${props.benefit.id})`}
          </div>
          <div
            className="dialog-text text-reset category-content-wrapper"
            ref={contentRef}
            style={{ visibility: isContentVisible ? 'visible' : 'hidden' }}
            dangerouslySetInnerHTML={{
              __html: _.unescape(props.benefitDetails.value?.description ?? ''),
            }}
          />
        </div>
        <div className="dialog-actions vertical">
          <button onClick={props.onClose} className="btn btn-outline">
            {translate(resources.close)}
          </button>
        </div>
      </Loader>
    </Dialog>
  );
}
